import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer,
} from 'recharts';
import AdminLayout from './AdminLayout';
import './Dashboard.css';
import API_BASE_URL from '../../config';
import '../../axios_overrite';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const cardData = [
  { label: 'Micro Organization',      key: 'micro',       iconClass: 'stat-icon-micro',       viewLink: '/admin/micro-organizations',      icon: <img src="/people-skin-type.png"  alt="Micro"       className="stat-icon-img" /> },
  { label: 'Mid-Scale Organization',  key: 'mid',         iconClass: 'stat-icon-mid',         viewLink: '/admin/mid-organizations',        icon: <img src="/Organization_1.png"    alt="Mid-Scale"   className="stat-icon-img" /> },
  { label: 'Large Organization',      key: 'large',       iconClass: 'stat-icon-large',       viewLink: '/admin/large-organizations',      icon: <img src="/business-building.png" alt="Large"       className="stat-icon-img" /> },
  { label: 'Enterprise Organization', key: 'enterprise',  iconClass: 'stat-icon-enterprise',  viewLink: '/admin/enterprise-organizations', icon: <img src="/city-buildings.png"    alt="Enterprise"  className="stat-icon-img" /> },
  { label: 'Nominations',             key: 'nominations', iconClass: 'stat-icon-nominations', viewLink: '/admin/nominations',              icon: <img src="/add-user-male.png"     alt="Nominations" className="stat-icon-img" /> },
  { label: 'Subscribes',              key: 'subscribers', iconClass: 'stat-icon-subscribers', viewLink: '/admin/insights',                 icon: <img src="/Users.png"             alt="Subscribers" className="stat-icon-img" /> },
];

const PIE_COLORS = ['#7c3aed', '#3b82f6', '#22c55e', '#f97316'];

// Edit the four organization labels directly here — they are NOT pulled from the backend.
// counts.micro / counts.mid / counts.large / counts.enterprise (the numbers) still come
// from the API, but these display names are fully controlled in this JS file.
const PIE_LABELS = {
  micro:      'Micro',
  mid:        'Mid',
  large:      'Large',
  enterprise: 'Enterprise',
};

// Full names shown in the right-side legend only
const PIE_LABELS_FULL = {
  micro:      'Micro Organization',
  mid:        'Mid-Scale Organization',
  large:      'Large Organization',
  enterprise: 'Enterprise Organization',
};

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const MIN_PX_PER_MONTH = 60;

function typeBadgeClass(type) {
  if (!type) return '';
  const t = type.toLowerCase();
  if (t.includes('micro'))      return 'type-micro';
  if (t.includes('mid'))        return 'type-mid';
  if (t.includes('large'))      return 'type-large';
  if (t.includes('enterprise')) return 'type-enterprise';
  return '';
}

function PillLegend({ payload }) {
  if (!payload || !payload.length) return null;
  return (
    <div className="chart-legend-row">
      {payload.map((entry, index) => (
        <div className="chart-legend-item" key={index}>
          <svg viewBox="0 0 18 5" aria-hidden="true">
            <rect x="0" y="0" width="18" height="5" rx="2.5" fill={entry.color} />
          </svg>
          <span className="chart-legend-label" style={{ color: entry.color }}>{entry.value}</span>
        </div>
      ))}
    </div>
  );
}

const tooltipStyle = {
  fontSize: 12,
  borderRadius: 8,
  border: '1px solid #ebedf2',
  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
};
const axisTickStyle = { fontSize: 12, fontFamily: 'inter', fill: '#000000' };

function computeYMax(data, keys) {
  let max = 0;
  data.forEach((row) => {
    keys.forEach((k) => {
      const v = row[k];
      if (typeof v === 'number' && v > max) max = v;
    });
  });
  if (max === 0) return 10;
  const step = max <= 20 ? 2 : max <= 50 ? 5 : max <= 100 ? 10 : max <= 200 ? 20 : max <= 500 ? 50 : 100;
  return Math.ceil((max + step) / step) * step;
}

// Sticky Y-axis + horizontally scrollable chart body.
// Change height and scrollWidth via props; Y-axis stays fixed while body scrolls.
function ScrollableLineChart({ data, scrollWidth, height, yMax, children }) {
  // Recharts will NOT render a YAxis if the chart has no Line children.
  // We clone every Line as a ghost (transparent, no dots) so the Y-axis renders.
  const ghostLines = React.Children.map(children, (child) =>
    React.isValidElement(child)
      ? React.cloneElement(child, {
          stroke: 'transparent',
          dot: false,
          activeDot: false,
          legendType: 'none',
        })
      : child
  );

  return (
    <div className="scrollable-chart-wrap">
      {/* Fixed Y-axis — ghost lines force recharts to compute & render the axis */}
      <div className="scrollable-chart-yaxis">
        <LineChart
          width={52} height={height} data={data}
          margin={{ top: 10, right: 0, left: 10, bottom: 30 }}
        >
          <YAxis tick={axisTickStyle} axisLine={false} tickLine={false}
            domain={[0, yMax]} allowDecimals={false} tickCount={8}
            width={36} padding={{ bottom: 20 }} />
          {ghostLines}
        </LineChart>
      </div>
      {/* Scrollable chart body — Y-axis hidden, X-axis visible */}
      <div className="scrollable-chart-body">
        <LineChart
          width={scrollWidth} height={height} data={data}
          margin={{ top: 10, right: 30, left: 0, bottom: 10 }}
        >
          <CartesianGrid strokeDasharray="4 4" stroke="#e8eaf0" vertical={false} />
          <XAxis dataKey="month" tick={axisTickStyle} axisLine={false} tickLine={false}
            dy={8} interval={0} padding={{ left: 40, right: 40 }} />
          <YAxis hide domain={[0, yMax]} allowDecimals={false} tickCount={8} padding={{ bottom: 20 }} />
          <Tooltip contentStyle={tooltipStyle} />
          {children}
        </LineChart>
      </div>
    </div>
  );
}


// slice is its own hoverable element (replaces the old CSS conic-gradient,
// which had no way to target individual segments for hover).
const DONUT_RADIUS = 80;
const DONUT_STROKE = 36;
const DONUT_CIRCUMFERENCE = 2 * Math.PI * DONUT_RADIUS;

function buildDonutSegments(data) {
  const total = data.reduce((s, d) => s + d.value, 0);
  let cumulative = 0;
  return data.map((d) => {
    const fraction = total ? d.value / total : 0;
    const length = fraction * DONUT_CIRCUMFERENCE;
    const offset = (cumulative / total || 0) * DONUT_CIRCUMFERENCE;
    cumulative += d.value;
    return {
      ...d,
      length,
      offset,
      pct: total ? Math.round(fraction * 100) : 0,
    };
  });
}

export default function AdminDashboard() {


  const [filter, setFilter] = useState(null);
  const [hoveredSlice, setHoveredSlice] = useState(null); // { index, x, y } or null

  const [counts,               setCounts]               = useState({ micro:0, mid:0, large:0, enterprise:0, nominations:0, subscribers:0 });
  const [orgOverviewData,      setOrgOverviewData]      = useState([]);
  const [nominationsTrendData, setNominationsTrendData] = useState([]);
  const [recentOrgs,           setRecentOrgs]           = useState([]);

 // REPLACE handleYearChange:
const handleYearChange = useCallback((startYear, startMonth, endYear, endMonth) => {
  setFilter({ startYear, startMonth, endYear, endMonth });
}, []);

  useEffect(() => {
    // REPLACE params:
const params = filter
  ? {
      startYear:  filter.startYear,
      startMonth: filter.startMonth,
      endYear:    filter.endYear,
      endMonth:   filter.endMonth,
    }
  : {};

    axios.get(`${API_BASE_URL}/api/admin/dashboard/counts`)
      .then((res) => setCounts((prev) => ({ ...prev, ...res.data })))
      .catch((err) => console.error('Dashboard counts error:', err));

    axios.get(`${API_BASE_URL}/api/admin/dashboard/org-monthly`, { params })
      .then((res) => setOrgOverviewData(res.data))
      .catch((err) => console.error('Org monthly error:', err));

    axios.get(`${API_BASE_URL}/api/admin/dashboard/nominations-subscribers-monthly`, { params })
      .then((res) => setNominationsTrendData(res.data))
      .catch((err) => console.error('Nominations+Subscribers monthly error:', err));

    axios.get(`${API_BASE_URL}/api/admin/dashboard/recent-organizations`)
      .then((res) => setRecentOrgs(res.data))
      .catch((err) => console.error('Recent orgs error:', err));

  }, [filter]);

  const pieData = [
    { name: PIE_LABELS.micro,      fullName: PIE_LABELS_FULL.micro,      value: counts.micro },
    { name: PIE_LABELS.mid,        fullName: PIE_LABELS_FULL.mid,        value: counts.mid },
    { name: PIE_LABELS.large,      fullName: PIE_LABELS_FULL.large,      value: counts.large },
    { name: PIE_LABELS.enterprise, fullName: PIE_LABELS_FULL.enterprise, value: counts.enterprise },
  ];
  const pieTotal = pieData.reduce((s, d) => s + d.value, 0);
  const donutSegments = buildDonutSegments(pieData);

  const ORG_KEYS = ['Micro Organization','Mid-Scale Organization','Large Organization','Enterprise Organization'];
  const NOM_KEYS = ['Nominations','Subscribes'];

  const orgYMax = computeYMax(orgOverviewData, ORG_KEYS);
  const nomYMax = computeYMax(nominationsTrendData, NOM_KEYS);

  const orgChartWidth = Math.max(orgOverviewData.length      * MIN_PX_PER_MONTH, 500);
  const nomChartWidth = Math.max(nominationsTrendData.length * MIN_PX_PER_MONTH, 500);
// REPLACE periodLabel:
const periodLabel = !filter
  ? 'All Data'
  : `${MONTHS[filter.startMonth]} ${filter.startYear} – ${MONTHS[filter.endMonth]} ${filter.endYear}`;

  const handleExportPDF = () => {
  const doc = new jsPDF();
  const pageW = doc.internal.pageSize.getWidth();

  // Title
  doc.setFontSize(18);
  doc.setTextColor(13, 27, 62);
  doc.text('Outstanding Workplaces', pageW / 2, 18, { align: 'center' });

  doc.setFontSize(11);
  doc.setTextColor(120, 120, 120);
  doc.text(`Dashboard Report — ${periodLabel}`, pageW / 2, 26, { align: 'center' });

  doc.setDrawColor(200, 200, 200);
  doc.line(14, 30, pageW - 14, 30);

  // ── Summary counts ──
  doc.setFontSize(12);
  doc.setTextColor(13, 27, 62);
  doc.text('Summary', 14, 38);

  autoTable(doc, {
    startY: 42,
    head: [['Category', 'Count']],
    body: [
      ['Micro Organization',      counts.micro],
      ['Mid-Scale Organization',  counts.mid],
      ['Large Organization',      counts.large],
      ['Enterprise Organization', counts.enterprise],
      ['Nominations',             counts.nominations],
      ['Subscribers',             counts.subscribers],
    ],
    headStyles:    { fillColor: [124, 58, 237], textColor: 255, fontSize: 10 },
    bodyStyles:    { fontSize: 10 },
    alternateRowStyles: { fillColor: [245, 243, 255] },
    margin: { left: 14, right: 14 },
  });

  // ── Org Overview chart data ──
  doc.setFontSize(12);
  doc.setTextColor(13, 27, 62);
  doc.text('Organizations Overview', 14, doc.lastAutoTable.finalY + 12);

  autoTable(doc, {
    startY: doc.lastAutoTable.finalY + 16,
    head: [['Month', 'Micro', 'Mid-Scale', 'Large', 'Enterprise']],
    body: orgOverviewData.map(row => [
      row.month,
      row['Micro Organization']      ?? '-',
      row['Mid-Scale Organization']  ?? '-',
      row['Large Organization']      ?? '-',
      row['Enterprise Organization'] ?? '-',
    ]),
    headStyles:    { fillColor: [124, 58, 237], textColor: 255, fontSize: 10 },
    bodyStyles:    { fontSize: 9 },
    alternateRowStyles: { fillColor: [245, 243, 255] },
    margin: { left: 14, right: 14 },
  });

  // ── Nominations & Subscribers trend ──
  doc.setFontSize(12);
  doc.setTextColor(13, 27, 62);
  doc.text('Nominations & Subscribes Trend', 14, doc.lastAutoTable.finalY + 12);

  autoTable(doc, {
    startY: doc.lastAutoTable.finalY + 16,
    head: [['Month', 'Nominations', 'Subscribes']],
    body: nominationsTrendData.map(row => [
      row.month,
      row['Nominations'] ?? '-',
      row['Subscribes']  ?? '-',
    ]),
    headStyles:    { fillColor: [236, 72, 153], textColor: 255, fontSize: 10 },
    bodyStyles:    { fontSize: 9 },
    alternateRowStyles: { fillColor: [255, 241, 249] },
    margin: { left: 14, right: 14 },
  });
 
  // ── Recent Organizations ──
  doc.setFontSize(12);
  doc.setTextColor(13, 27, 62);
  doc.text('Recent Organizations', 14, doc.lastAutoTable.finalY + 12);

  autoTable(doc, {
    startY: doc.lastAutoTable.finalY + 16,
    head: [['#', 'Organization Name', 'Type', 'Status']],
    body: recentOrgs.map(org => [org.id, org.name, org.type, org.status]),
    headStyles:    { fillColor: [124, 58, 237], textColor: 255, fontSize: 10 },
    bodyStyles:    { fontSize: 9 },
    alternateRowStyles: { fillColor: [245, 243, 255] },
    margin: { left: 14, right: 14 },
  });

  // ── Footer ──
  doc.setFontSize(8);
  doc.setTextColor(160, 160, 160);
  doc.text(
    `Generated on ${new Date().toLocaleString()}`,
    pageW / 2,
    doc.internal.pageSize.getHeight() - 8,
    { align: 'center' }
  );

 doc.save(`Dashboard Report.pdf`);
};

  return (
    <AdminLayout onYearChange={handleYearChange} onExportPDF={handleExportPDF}>
      <div className="admin-dashboard-main">

        {/* Stat cards */}
        <div className="dashboard-stats-grid">
          {cardData.map((card) => (
            <a className="dashboard-stat-card" key={card.key} href={card.viewLink}>
              <div className={`stat-icon-wrap ${card.iconClass}`}>{card.icon}</div>
              <div className="stat-info">
                <div className="stat-count">{counts[card.key]}</div>
                <div className="stat-label">{card.label}</div>
              </div>
              <div className="stat-arrow">›</div>
            </a>
          ))}
        </div>

        {/* All 4 chart cards in one 2×2 grid — ensures row 1 = row 2 height */}
        <div className="dashboard-all-charts">

          {/* Cell 1 — Pie */}
          <div className="dashboard-bottom-card pie-card">
            <div className="bottom-card-title">Organizations Type</div>
            <div className="pie-chart-wrap">
              <div className="css-donut-wrap" onMouseLeave={() => setHoveredSlice(null)}>
                <svg viewBox="0 0 200 200" className="donut-svg">
                  <g transform="rotate(-90 100 100)">
                    {!pieTotal && (
                      <circle cx="100" cy="100" r={DONUT_RADIUS}
                        fill="none" stroke="#ebedf2" strokeWidth={DONUT_STROKE} />
                    )}
                    {donutSegments.map((seg, i) => (
                      seg.value > 0 && (
                        <circle
                          key={seg.name}
                          cx="100" cy="100" r={DONUT_RADIUS}
                          fill="none"
                          stroke={PIE_COLORS[i % PIE_COLORS.length]}
                          strokeWidth={DONUT_STROKE}
                          strokeDasharray={`${seg.length} ${DONUT_CIRCUMFERENCE - seg.length}`}
                          strokeDashoffset={-seg.offset}
                          className="donut-segment"
                          onMouseEnter={(e) => {
                            const rect = e.currentTarget.ownerSVGElement.getBoundingClientRect();
                            setHoveredSlice({ index: i, x: e.clientX - rect.left, y: e.clientY - rect.top });
                          }}
                          onMouseMove={(e) => {
                            const rect = e.currentTarget.ownerSVGElement.getBoundingClientRect();
                            setHoveredSlice({ index: i, x: e.clientX - rect.left, y: e.clientY - rect.top });
                          }}
                        />
                      )
                    ))}
                  </g>
                </svg>
                <div className="css-donut-hole">
                  <span className="css-donut-total-label">Total</span>
                  <span className="css-donut-total-value">{pieTotal}</span>
                </div>
                {hoveredSlice && (
                  <div className="donut-tooltip" style={{ left: hoveredSlice.x, top: hoveredSlice.y }}>
                    <span className="donut-tooltip-dot" style={{ background: PIE_COLORS[hoveredSlice.index % PIE_COLORS.length] }} />
                    {donutSegments[hoveredSlice.index].name}: <strong>{donutSegments[hoveredSlice.index].value}</strong>
                  </div>
                )}
              </div>
              <div className="pie-legend">
                {pieData.map((entry, i) => (
                  <div
                    className={`pie-legend-item ${hoveredSlice?.index === i ? 'pie-legend-item-active' : ''}`}
                    key={entry.name}
                  >
                    <div className={`pie-dot pie-dot-${i}`} />
                    {entry.fullName}
                    <span className="pie-legend-pct">
                      {entry.value} ({pieTotal ? Math.round((entry.value / pieTotal) * 100) : 0}%)
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Cell 2 — Org Overview */}
          <div className="dashboard-chart-card org-chart-card">
            <div className="chart-card-title">
              Organizations Overview
              <span className="chart-period-label">— {periodLabel}</span>
            </div>
            <PillLegend payload={[
              { value: 'Enterprise Organization', color: '#f97316' },
              { value: 'Large Organization',      color: '#22c55e' },
              { value: 'Micro Organization',      color: '#7c3aed' },
              { value: 'Mid-Scale Organization',  color: '#3b82f6' },
            ]} />
            <ScrollableLineChart data={orgOverviewData} scrollWidth={orgChartWidth} height={280} yMax={orgYMax}>
              <Line type="linear" dataKey="Micro Organization"      stroke="#7c3aed" strokeWidth={2} dot={{ r: 4, fill: '#7c3aed', stroke: '#fff', strokeWidth: 1.5 }} activeDot={{ r: 5 }} connectNulls={true} />
              <Line type="linear" dataKey="Mid-Scale Organization"  stroke="#3b82f6" strokeWidth={2} dot={{ r: 4, fill: '#3b82f6', stroke: '#fff', strokeWidth: 1.5 }} activeDot={{ r: 5 }} connectNulls={true} />
              <Line type="linear" dataKey="Large Organization"      stroke="#22c55e" strokeWidth={2} dot={{ r: 4, fill: '#22c55e', stroke: '#fff', strokeWidth: 1.5 }} activeDot={{ r: 5 }} connectNulls={true} />
              <Line type="linear" dataKey="Enterprise Organization" stroke="#f97316" strokeWidth={2} dot={{ r: 4, fill: '#f97316', stroke: '#fff', strokeWidth: 1.5 }} activeDot={{ r: 5 }} connectNulls={true} />
            </ScrollableLineChart>
          </div>

          {/* Cell 3 — Nominations */}
          <div className="nom-chart-card">
            <div className="chart-card-title">
              Nominations &amp; Subscribes Trend
              <span className="chart-period-label">— {periodLabel}</span>
            </div>
            <PillLegend payload={[
              { value: 'Nominations', color: '#ec4899' },
              { value: 'Subscribes',  color: '#14b8a6' },
            ]} />
            <ScrollableLineChart data={nominationsTrendData} scrollWidth={nomChartWidth} height={280} yMax={nomYMax}>
              <Line type="linear" dataKey="Nominations" stroke="#ec4899" strokeWidth={2}
                dot={{ r: 4, fill: '#ec4899', stroke: '#fff', strokeWidth: 1.5 }} activeDot={{ r: 5 }} connectNulls={true} />
              <Line type="linear" dataKey="Subscribes"  stroke="#14b8a6" strokeWidth={2}
                dot={{ r: 4, fill: '#14b8a6', stroke: '#fff', strokeWidth: 1.5 }} activeDot={{ r: 5 }} connectNulls={true} />
            </ScrollableLineChart>
          </div>

          {/* Cell 4 — Recent Orgs */}
          <div className="dashboard-bottom-card recent-orgs-card">
            <div className="bottom-card-title">Recent Organizations</div>
            <div className="recent-orgs-table-wrap">
              <table className="recent-orgs-table">
                <thead>
                  <tr><th>S.No</th><th>Organization Name</th><th>Type</th><th>Status</th></tr>
                </thead>
                <tbody>
                  {recentOrgs.map((org) => (
                    <tr key={org.id}>
                      <td>{org.id}</td>
                      <td>{org.name}</td>
                      <td><span className={`type-badge ${typeBadgeClass(org.type)}`}>{org.type}</span></td>
                      <td>
                        <span className={`status-badge status-${org.status.toLowerCase()}`}>
                          <span className="status-dot" />{org.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

        </div>
      </div>
    </AdminLayout>
  );    
}